
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import RichTextDisplay from "@/components/Shared/RichTextDisplay.vue";
import {
  autoLinkNoteHtml,
  isNoteEmpty,
  normalizeUrl,
  plainTextToNoteHtml,
  sanitizeNoteHtml,
  toNoteHtml,
} from "@/utils/richText";

interface FormatButton {
  command: string;
  icon: string;
  title: string;
}

/**
 * Poznámka s jednoduchým formátováním a odkazy.
 *
 * Hodnota se ukládá jako omezená podmnožina HTML do stávajícího textového pole,
 * takže starší poznámky v čistém textu se zobrazí i nadále správně. Veškerý
 * obsah prochází sanitizací (utils/richText.ts) - jak při zobrazení, tak před
 * vyemitováním na server.
 */
@Component({
  name: "RichTextInput",
  components: {
    RichTextDisplay,
  },
})
export default class RichTextInput extends Vue {
  @Prop()
  value: string | null | undefined;

  @Prop({ default: "" })
  label: string;

  @Prop({ default: null })
  icon: string | null;

  @Prop({ type: Boolean, default: false })
  readonly: boolean;

  @Prop({ type: Boolean, default: false })
  disabled: boolean;

  @Prop({ default: "56px" })
  minHeight: string;

  formatButtons: FormatButton[] = [
    { command: "bold", icon: "mdi-format-bold", title: "Tučně (Ctrl+B)" },
    { command: "italic", icon: "mdi-format-italic", title: "Kurzíva (Ctrl+I)" },
    {
      command: "underline",
      icon: "mdi-format-underline",
      title: "Podtržení (Ctrl+U)",
    },
    {
      command: "strikeThrough",
      icon: "mdi-format-strikethrough",
      title: "Přeškrtnutí",
    },
    {
      command: "insertUnorderedList",
      icon: "mdi-format-list-bulleted",
      title: "Odrážky",
    },
    {
      command: "insertOrderedList",
      icon: "mdi-format-list-numbered",
      title: "Číslovaný seznam",
    },
  ];

  activeFormats: Record<string, boolean> = {};

  linkDialog = false;
  linkUrl = "";
  linkText = "";
  linkError = "";
  isEditingLink = false;

  /** Rozsah, na kterém stál kurzor před otevřením dialogu odkazu. */
  private savedRange: Range | null = null;

  /** Naposledy vyemitovaná hodnota - brání přepsání obsahu při psaní. */
  private lastEmitted: string | null = null;

  get isEditable(): boolean {
    return !this.readonly && !this.disabled;
  }

  /**
   * Musí to být metoda, ne getter. Getter by se stal computed property, a ta se
   * cachuje podle reaktivních závislostí - $refs mezi ně nepatří. V režimu
   * náhledu editor neexistuje, computed by si zapamatovala null a už nikdy se
   * nepřepočítala, takže by komponenta přestala emitovat změny.
   */
  private getEditorElement(): HTMLElement | null {
    return (this.$refs.editor as HTMLElement | undefined) ?? null;
  }

  mounted() {
    this.syncEditorContent();
  }

  @Watch("value")
  onValueChanged() {
    this.syncEditorContent();
  }

  @Watch("isEditable")
  onEditableChanged() {
    // Přepnutí z náhledu do editace vytvoří editor až v dalším ticku.
    this.$nextTick(() => this.syncEditorContent());
  }

  /** Přenese hodnotu z modelu do editoru, aniž by rušil právě psaný text. */
  private syncEditorContent() {
    const editor = this.getEditorElement();
    if (editor == null) {
      return;
    }

    // Text, který uživatel právě píše, se nepřepisuje - zahodilo by to kurzor.
    // Srovnání s lastEmitted samo nestačí: přepnutím z náhledu do editace se
    // editor vytvoří znovu prázdný, zatímco lastEmitted na komponentě přežije,
    // takže by se do nového editoru hodnota už nikdy nedostala. Rozhoduje proto
    // i fokus - bez něj žádný kurzor není a přepis nemá co pokazit.
    if (this.value === this.lastEmitted && document.activeElement === editor) {
      return;
    }

    const html = toNoteHtml(this.value);
    if (editor.innerHTML != html) {
      editor.innerHTML = html;
    }
  }

  private emitValue(html: string) {
    const value = isNoteEmpty(html) ? "" : html;
    if (value === this.lastEmitted) {
      return;
    }

    this.lastEmitted = value;
    this.$emit("input", value);
  }

  onEditorInput() {
    const editor = this.getEditorElement();
    if (editor == null) {
      return;
    }

    // Sanitizuje se jen emitovaná hodnota - obsah editoru se během psaní
    // nepřepisuje, aby kurzor zůstal na místě.
    this.emitValue(sanitizeNoteHtml(editor.innerHTML));
    this.refreshActiveFormats();
  }

  onEditorBlur() {
    const editor = this.getEditorElement();
    if (editor == null) {
      return;
    }

    // Holé adresy se zaklikatelní až při opuštění pole, aby psaní URL nerušilo.
    const html = autoLinkNoteHtml(sanitizeNoteHtml(editor.innerHTML));
    if (editor.innerHTML != html) {
      editor.innerHTML = html;
    }

    this.emitValue(html);
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const data = event.clipboardData;
    if (data == null) {
      return;
    }

    // Vložené HTML se propustí přes allowlist, jinak se použije čistý text.
    const pastedHtml = data.getData("text/html");
    const html =
      pastedHtml != ""
        ? sanitizeNoteHtml(pastedHtml)
        : plainTextToNoteHtml(data.getData("text/plain"));

    this.execCommand("insertHTML", html);
    this.onEditorInput();
  }

  runCommand(command: string) {
    this.execCommand(command);
    this.onEditorInput();
  }

  clearFormatting() {
    this.execCommand("removeFormat");
    this.execCommand("unlink");
    this.onEditorInput();
  }

  private execCommand(command: string, argument?: string) {
    this.getEditorElement()?.focus();

    // Bez tohoto produkují prohlížeče <span style="...">, které sanitizace zahodí.
    try {
      document.execCommand("styleWithCSS", false, "false");
    } catch {
      // Nepodporováno - příkazy níže stejně fungují.
    }

    document.execCommand(command, false, argument);
  }

  refreshActiveFormats() {
    const formats: Record<string, boolean> = {};
    for (const button of this.formatButtons) {
      try {
        formats[button.command] = document.queryCommandState(button.command);
      } catch {
        formats[button.command] = false;
      }
    }

    this.activeFormats = formats;
  }

  /** Uloží rozsah kurzoru - dialog editoru odebere fokus i výběr. */
  private saveSelection() {
    const editor = this.getEditorElement();
    const selection = window.getSelection();
    if (editor == null || selection == null || selection.rangeCount == 0) {
      this.savedRange = null;
      return;
    }

    const range = selection.getRangeAt(0);
    this.savedRange = editor.contains(range.commonAncestorContainer)
      ? range.cloneRange()
      : null;
  }

  private restoreSelection() {
    if (this.savedRange == null) {
      return;
    }

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(this.savedRange);
  }

  /** Odkaz, ve kterém stojí kurzor - kvůli úpravě místo zanoření. */
  private getSelectedLink(): HTMLAnchorElement | null {
    const editor = this.getEditorElement();
    if (editor == null || this.savedRange == null) {
      return null;
    }

    const node = this.savedRange.commonAncestorContainer;
    const element =
      node.nodeType == Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;

    const link = element?.closest("a") ?? null;

    return link != null && editor.contains(link)
      ? (link as HTMLAnchorElement)
      : null;
  }

  openLinkDialog() {
    this.saveSelection();
    this.linkError = "";

    const link = this.getSelectedLink();
    this.isEditingLink = link != null;

    if (link != null) {
      this.linkUrl = link.getAttribute("href") ?? "";
      this.linkText = link.textContent ?? "";
    } else {
      this.linkUrl = "";
      this.linkText = this.savedRange?.toString() ?? "";
    }

    this.linkDialog = true;
  }

  closeLinkDialog() {
    this.linkDialog = false;
    this.linkError = "";
  }

  confirmLinkDialog() {
    const url = normalizeUrl(this.linkUrl);
    if (url == null) {
      this.linkError = "Zadej platnou adresu (http, https nebo e-mail).";
      return;
    }

    const text = this.linkText.trim();

    // Mezera za novým odkazem dovolí psát dál mimo něj. U úpravy stávajícího
    // odkazu se nepřidává, jinak by se mezery při každé úpravě nabalovaly.
    const trailingSpace = this.isEditingLink ? "" : "&nbsp;";

    const anchor =
      '<a href="' +
      url.replace(/"/g, "&quot;") +
      '" target="_blank" rel="noopener noreferrer">' +
      plainTextToNoteHtml(text != "" ? text : this.linkUrl.trim()) +
      "</a>" +
      trailingSpace;

    this.replaceSelectionWith(anchor);
  }

  removeLink() {
    const link = this.getSelectedLink();
    if (link == null) {
      this.closeLinkDialog();
      return;
    }

    // Odkaz se ruší příkazem unlink. Přepis přes insertHTML by tu neprošel -
    // prohlížeč vložení zahodí, pokud se vkládaný text rovná vybranému.
    this.selectWholeLink(link);
    this.linkDialog = false;
    this.linkError = "";

    this.$nextTick(() => {
      this.getEditorElement()?.focus();
      this.restoreSelection();
      this.execCommand("unlink");
      this.onEditorInput();
    });
  }

  /** Rozšíří uložený výběr na celý odkaz, aby se přepsal a nezanořil. */
  private selectWholeLink(link: HTMLAnchorElement) {
    const range = document.createRange();
    range.selectNode(link);
    this.savedRange = range;
  }

  private replaceSelectionWith(html: string) {
    const existingLink = this.getSelectedLink();
    if (existingLink != null) {
      this.selectWholeLink(existingLink);
    }

    this.linkDialog = false;
    this.linkError = "";

    // Dialog vrací fokus až po zavření, teprve pak lze obnovit výběr.
    this.$nextTick(() => {
      this.getEditorElement()?.focus();
      this.restoreSelection();
      this.execCommand("insertHTML", html);
      this.onEditorInput();
    });
  }
}
